import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, Activity, ChevronDown, ChevronRight, Play, DollarSign, BarChart3, Target, TrendingDown, AlertCircle, Filter, Check, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';

const Backtesting = () => {
  const { fetchPost } = useApi();
  const darkMode = typeof document !== 'undefined' && document.body.classList.contains('global-dark-mode');
  // Calculate dates: 2 weeks back for start_date, tomorrow for end_date
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const twoWeeksAgo = new Date(today);
  twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
  
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    start_date: formatDate(twoWeeksAgo),
    end_date: formatDate(tomorrow),
    spread: 5,
    volume: 30,
    fixing_i_margin: 0,
    fixing_ii_margin: 1000,
    model_version: 'v2.0.0',
    fixing_i_margin_type: 'fixed',
    fixing_i_mae_multiplier: 1.0
  });

  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedResiduals, setExpandedResiduals] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState(false);
  const [displayHours1to24, setDisplayHours1to24] = useState(true);
  const [filters, setFilters] = useState({
    pse_pv_avg: { min: null, max: null },
    pse_wind_avg: { min: null, max: null },
    zrb_avg: { min: null, max: null },
    pse_pv_avg_delta_1d: { min: null, max: null },
    pse_wind_avg_delta_1d: { min: null, max: null }
  });
  const [filterBounds, setFilterBounds] = useState({
    pse_pv_avg: { min: 0, max: 10000 },
    pse_wind_avg: { min: 0, max: 10000 },
    zrb_avg: { min: -10000, max: 10000 },
    pse_pv_avg_delta_1d: { min: -5000, max: 5000 },
    pse_wind_avg_delta_1d: { min: -5000, max: 5000 }
  });

  // Calculate filter bounds when results change
  useEffect(() => {
    if (results && results.results) {
      const bounds = {
        pse_pv_avg: { min: Infinity, max: -Infinity },
        pse_wind_avg: { min: Infinity, max: -Infinity },
        zrb_avg: { min: Infinity, max: -Infinity },
        pse_pv_avg_delta_1d: { min: Infinity, max: -Infinity },
        pse_wind_avg_delta_1d: { min: Infinity, max: -Infinity }
      };

      Object.entries(results.results).forEach(([date, data]) => {
        if (date !== 'summary' && data.forecasts) {
          const forecasts = data.forecasts;
          Object.keys(bounds).forEach(key => {
            if (forecasts[key] !== null && forecasts[key] !== undefined) {
              bounds[key].min = Math.min(bounds[key].min, forecasts[key]);
              bounds[key].max = Math.max(bounds[key].max, forecasts[key]);
            }
          });
        }
      });

      // Set reasonable bounds
      Object.keys(bounds).forEach(key => {
        if (bounds[key].min === Infinity) {
          bounds[key].min = 0;
          bounds[key].max = 100;
        }
      });

      setFilterBounds(bounds);
      
      // Initialize filters to full range
      const initialFilters = {};
      Object.keys(bounds).forEach(key => {
        initialFilters[key] = { min: bounds[key].min, max: bounds[key].max };
      });
      setFilters(initialFilters);
    }
  }, [results]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const runBacktesting = async (overrideFormData = null) => {
    setIsLoading(true);
    setError(null);
    setResults(null);
    
    try {
      const dataToUse = overrideFormData || formData;
      
      const formDataToSend = new FormData();
      Object.entries(dataToUse).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetchPost('/backtesting', formDataToSend);

      const data = await response.json();
      
      if (data.status === 'success') {
        setResults(data);
      } else {
        setError(data.message || 'Backtesting failed');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('Error running backtesting:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRow = (date) => {
    setExpandedRows(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const isRowFiltered = (data) => {
    if (!data.forecasts) return false;
    
    const forecasts = data.forecasts;
    for (const key of Object.keys(filters)) {
      const filterRange = filters[key];
      const value = forecasts[key];
      
      if (value !== null && value !== undefined) {
        if (value < filterRange.min || value > filterRange.max) {
          return true;
        }
      }
    }
    
    return false;
  };

  const handleFilterChange = (field, side, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [side]: parseFloat(value)
      }
    }));
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return `${num.toFixed(1)}%`;
  };

  const getMAEColorClass = (mae, allMAEValues) => {
    if (mae === null || mae === undefined || allMAEValues.length === 0) return 'bg-gray-50';
    
    // Filter out null/undefined values and calculate min/max
    const validValues = allMAEValues.filter(v => v !== null && v !== undefined);
    if (validValues.length === 0) return 'bg-gray-50';
    
    const minMAE = Math.min(...validValues);
    const maxMAE = Math.max(...validValues);
    
    // If all values are the same, return neutral color
    if (minMAE === maxMAE) return 'bg-gray-50';
    
    // Normalize the MAE value between 0 and 1
    const normalized = (mae - minMAE) / (maxMAE - minMAE);
    
    // Define color classes from green (low MAE) to red (high MAE)
    if (normalized <= 0.2) return 'bg-green-100';
    else if (normalized <= 0.4) return 'bg-green-50';
    else if (normalized <= 0.6) return 'bg-yellow-50';
    else if (normalized <= 0.8) return 'bg-orange-50';
    else return 'bg-red-100';
  };

  
  const getResidualDates = () => {
    if (!results || !results.results) return { fixI: [], fixII: [] };
    
    const fixIDates = [];
    const fixIIDates = [];
    
    Object.entries(results.results).forEach(([date, data]) => {
      if (data && date !== 'summary') {
        // Check for Fixing I residuals
        if (data.fixing_i_results) {
          const hasResidual = Object.values(data.fixing_i_results).some(hourData => {
            if (typeof hourData === 'object') {
              return Object.entries(hourData).some(([key, value]) => 
                key.includes('_fixing_i_residual') && value && value !== 0
              );
            }
            return false;
          });
          if (hasResidual) fixIDates.push(date);
        }
        
        // Check for Fixing II residuals
        if (data.fixing_ii_results) {
          const hasResidual = Object.values(data.fixing_ii_results).some(hourData => {
            if (typeof hourData === 'object') {
              return Object.entries(hourData).some(([key, value]) => 
                key.includes('_fixing_ii_residual') && value && value !== 0
              );
            }
            return false;
          });
          if (hasResidual) fixIIDates.push(date);
        }
      }
    });
    
    return { fixI: fixIDates.sort(), fixII: fixIIDates.sort() };
  };

  const renderDetailedResults = (data, level = 0) => {
    return Object.entries(data).map(([key, value]) => {
      const indent = level * 20;
      
      if (typeof value === 'object' && value !== null) {
        return (
          <div key={key} style={{ marginLeft: `${indent}px` }}>
            <div className="font-semibold text-gray-700 mt-2">{key}:</div>
            {renderDetailedResults(value, level + 1)}
          </div>
        );
      }
      
      // Format key for better display
      const displayKey = key.replace(/_/g, ' ').replace(/h(\d+)/g, 'h$1');
      
      return (
        <div key={key} style={{ marginLeft: `${indent}px` }} className="text-sm text-gray-600">
          <span className="font-medium">{displayKey}:</span> {formatNumber(value)}
        </div>
      );
    });
  };

  const renderFixingTable = (fixingResults, fixingType, action, volume, date, basePrice) => {
    if (!fixingResults) return null;

    // Check if this is Fixing II and if we should use quarterly display
    const isFixingII = fixingType === 'II';
    const dateObj = date ? new Date(date) : null;
    const useQuarterlyDisplay = isFixingII && dateObj && dateObj >= new Date('2025-10-01');

    // Extract hourly data
    const hourlyData = [];
    for (let hour = 0; hour < 24; hour++) {
      const hourKey = `h${hour}`;
      const hourData = fixingResults[hourKey];
      if (hourData && typeof hourData === 'object') {
        hourlyData.push({
          hour,
          ...hourData
        });
      }
    }

    if (hourlyData.length === 0) return null;

    // Check if this is Fixing I or Fixing II
    const isFixingI = fixingType === 'I';

    // Check if any orders were executed
    const hasExecutedOrders = hourlyData.some(h => {
      const residualKey = `h${h.hour}_fixing_${fixingType.toLowerCase()}_residual`;
      const residual = h[residualKey];
      // Order was executed if residual is 0 (or undefined for hours where order wasn't placed)
      return residual === 0 && h[`pred_${h.hour}`] !== undefined;
    });

    // For quarterly display (Fixing II after 2025-10-01)
    if (useQuarterlyDisplay) {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hour</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quarter</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Prediction</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Fix II Margin</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Pred + Margin</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Fix II Price (15min)</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Executed</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Volume</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Residual</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quarter PnL</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {hourlyData.map((hour) => {
                const displayHour = displayHours1to24 ? hour.hour + 1 : hour.hour;
                const quartersKey = `h${hour.hour}_quarters`;
                const quarters = hour[quartersKey];

                if (!quarters) {
                  // No quarterly data - show single row with hourly data
                  const residualKey = `h${hour.hour}_fixing_ii_residual`;
                  const residual = hour[residualKey];
                  const predKey = `pred_${hour.hour}`;
                  const marginKey = `h${hour.hour}_fixing_ii_margin`;

                  return (
                    <tr key={hour.hour}>
                      <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900" rowSpan={1}>
                        {displayHour}:00
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">-</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                        {formatNumber(hour[predKey])}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                        {formatNumber(hour[marginKey])}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">-</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">-</td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">-</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                        {formatNumber(residual || 0)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                        {formatNumber(residual || 0)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                        {formatNumber(0)}
                      </td>
                    </tr>
                  );
                }

                // Render quarterly data
                const quarterRows = [];
                const predKey = `pred_${hour.hour}`;
                const marginKey = `h${hour.hour}_fixing_ii_margin`;
                const hourlyPred = hour[predKey];
                const hourlyMargin = hour[marginKey];

                for (let q = 1; q <= 4; q++) {
                  const quarterData = quarters[`q${q}`];
                  if (quarterData) {
                    const minutes = q * 15;
                    let quarterTime;
                    if (minutes === 60) {
                      if (hour.hour === 23) {
                        quarterTime = "24:00";
                      } else {
                        quarterTime = `${displayHour + 1}:00`;
                      }
                    } else {
                      quarterTime = `${displayHour}:${minutes.toString().padStart(2, '0')}`;
                    }

                    const qPredWithMargin = quarterData[`q${q}_pred_with_margin`];
                    const qPrice = quarterData[`q${q}_fixing_ii_price`];
                    const qResidual = quarterData[`q${q}_residual`];
                    const qCost = quarterData[`q${q}_cost`];
                    const fixingIResidual = hour[`h${hour.hour}_fixing_i_residual`] || 0;
                    const qVolume = fixingIResidual / 4; // Each quarter gets 1/4 of volume
                    const isExecuted = qResidual === 0 && qCost > 0;

                    // Calculate quarterly PnL based on action and base price
                    let qPnL = 0;

                    if (isExecuted && basePrice > 0 && qVolume > 0) {
                      // Calculate costs
                      const baseCost = basePrice * qVolume;
                      const quarterCost = qPrice * qVolume;

                      if (action === 'sell') {
                        // Selling action: PnL = base_cost - quarter_cost
                        qPnL = baseCost - quarterCost;
                      } else if (action === 'buy') {
                        // Buying action: PnL = quarter_cost - base_cost
                        qPnL = quarterCost - baseCost;
                      }
                    }

                    quarterRows.push(
                      <tr key={`${hour.hour}-q${q}`}>
                        {q === 1 && (
                          <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 border-r" rowSpan={4}>
                            {displayHour}:00
                          </td>
                        )}
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                          {quarterTime}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                          {formatNumber(hourlyPred)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                          {formatNumber(hourlyMargin)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900 font-semibold">
                          {formatNumber(qPredWithMargin)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                          {formatNumber(qPrice)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-center">
                          {isExecuted ? (
                            <Check className="inline-block text-green-600" size={16} />
                          ) : (
                            <X className="inline-block text-red-600" size={16} />
                          )}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                          {formatNumber(isExecuted ? qVolume : 0)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                          {formatNumber(qResidual || 0)}
                        </td>
                        <td className={`px-4 py-2 whitespace-nowrap text-sm text-right font-semibold ${
                          qPnL > 0 ? 'text-green-600' : qPnL < 0 ? 'text-red-600' : 'text-gray-900'
                        }`}>
                          {formatNumber(qPnL)}
                        </td>
                      </tr>
                    );
                  }
                }

                return quarterRows;
              })}
            </tbody>
            {hourlyData.length > 0 && (
              <tfoot className="bg-gray-100">
                <tr>
                  <td colSpan={9} className="px-4 py-2 text-right text-sm font-medium text-gray-700">
                    Total Fixing II PnL:
                  </td>
                  <td className={`px-4 py-2 text-right text-sm font-bold ${
                    (() => {
                      const totalPnL = hourlyData.reduce((sum, h) => {
                        const pnlKey = `h${h.hour}_fixing_ii_pnl`;
                        return sum + (h[pnlKey] || 0);
                      }, 0);
                      return totalPnL > 0 ? 'text-green-600' :
                             totalPnL < 0 ? 'text-red-600' :
                             'text-gray-900';
                    })()
                  }`}>
                    {formatNumber(hourlyData.reduce((sum, h) => {
                      const pnlKey = `h${h.hour}_fixing_ii_pnl`;
                      return sum + (h[pnlKey] || 0);
                    }, 0))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      );
    }

    // Original hourly display for Fixing I or Fixing II before 2025-10-01
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hour</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{isFixingI ? 'Prediction' : 'Fix I Price'}</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Fixing {fixingType} Price</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{isFixingI ? 'Fix I Margin' : 'Fix II Margin'}</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{isFixingI ? 'Pred + Margin' : 'Fix I + Margin'}</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Executed</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Volume</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Residual</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Hourly PnL</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {hourlyData.map((hour) => {
              const displayHour = displayHours1to24 ? hour.hour + 1 : hour.hour;
              const fixingPriceKey = `h${hour.hour}_fixing_${fixingType.toLowerCase()}_price`;
              const residualKey = `h${hour.hour}_fixing_${fixingType.toLowerCase()}_residual`;

              // For Fixing I, use prediction data; for Fixing II, use fixing I price data
              const predKey = `pred_${hour.hour}`;
              const fixingIPriceKey = `h${hour.hour}_fixing_i_price`;
              const predWithMarginKey = `pred_${hour.hour}_with_margin`;
              const fixingIPriceWithMarginKey = `h${hour.hour}_fixing_i_price_with_margin`;
              const appliedMarginKey = `h${hour.hour}_applied_margin`;

              // Get the appropriate values based on fixing type
              const baseValue = isFixingI ? hour[predKey] : hour[fixingIPriceKey];
              const fixingPrice = hour[fixingPriceKey];
              const residual = hour[residualKey];
              const valueWithMargin = isFixingI ? hour[predWithMarginKey] : hour[fixingIPriceWithMarginKey];
              const fixingIIMarginKey = `h${hour.hour}_fixing_ii_margin`;
              const margin = isFixingI ? hour[appliedMarginKey] : hour[fixingIIMarginKey];

              // Get PnL for the hour
              const pnlKey = `h${hour.hour}_fixing_${fixingType.toLowerCase()}_pnl`;
              const hourlyPnL = hour[pnlKey];

              // Calculate executed volume (volume - residual)
              const executedVolume = residual !== undefined ? volume - residual : 0;
              const isExecuted = executedVolume > 0;

              return (
                <tr key={hour.hour}>
                  <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                    {displayHour}:00
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatNumber(baseValue)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatNumber(fixingPrice)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatNumber(margin)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900 font-semibold">
                    {formatNumber(valueWithMargin)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-center">
                    {isExecuted ? (
                      <Check className="inline-block text-green-600" size={16} />
                    ) : (
                      <X className="inline-block text-red-600" size={16} />
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900 font-semibold">
                    {formatNumber(executedVolume)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatNumber(residual || 0)}
                  </td>
                  <td className={`px-4 py-2 whitespace-nowrap text-sm text-right font-semibold ${
                    hourlyPnL > 0 ? 'text-green-600' :
                    hourlyPnL < 0 ? 'text-red-600' :
                    'text-gray-900'
                  }`}>
                    {formatNumber(hourlyPnL || 0)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {hourlyData.length > 0 && (
            <tfoot className="bg-gray-100">
              <tr>
                <td colSpan={8} className="px-4 py-2 text-right text-sm font-medium text-gray-700">
                  Total {fixingType === 'I' ? 'Fixing I' : 'Fixing II'} PnL:
                </td>
                <td className={`px-4 py-2 text-right text-sm font-bold ${
                  (() => {
                    const totalPnL = hourlyData.reduce((sum, h) => {
                      const pnlKey = `h${h.hour}_fixing_${fixingType.toLowerCase()}_pnl`;
                      return sum + (h[pnlKey] || 0);
                    }, 0);
                    return totalPnL > 0 ? 'text-green-600' :
                           totalPnL < 0 ? 'text-red-600' :
                           'text-gray-900';
                  })()
                }`}>
                  {formatNumber(hourlyData.reduce((sum, h) => {
                    const pnlKey = `h${h.hour}_fixing_${fixingType.toLowerCase()}_pnl`;
                    return sum + (h[pnlKey] || 0);
                  }, 0))}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
        {!hasExecutedOrders && (
          <div className="text-center py-4 text-sm text-gray-500">
            No orders executed during Fixing {fixingType}
          </div>
        )}
        {!isFixingI && hourlyData.length === 0 && (
          <div className="text-center py-4 text-sm text-gray-500">
            All orders were executed during Fixing I - no residuals for Fixing II
          </div>
        )}
      </div>
    );
  };

  const renderDateRow = (date, data) => {
    const isExpanded = expandedRows[date];
    const topLevelKeys = Object.keys(data).filter(
      key => !['fixing_i_results', 'fixing_ii_results', 'balancing_market_results', 'forecasts'].includes(key)
    );
    const isPnLPositive = data.daily_pnl > 0;
    const isPnLNegative = data.daily_pnl < 0;
    const isFiltered = isRowFiltered(data);
    const isIdle = data.action === 'idle';

    // Calculate Fixing II residual count
    let fixingIIResidualCount = 0;
    if (data.fixing_ii_results) {
      // Check if this is quarterly data (from 2025-10-01 onwards)
      const dateObj = new Date(date);
      const useQuarterlyData = dateObj >= new Date('2025-10-01');

      for (let hour = 0; hour < 24; hour++) {
        const hourKey = `h${hour}`;
        const hourData = data.fixing_ii_results[hourKey];

        if (hourData && typeof hourData === 'object') {
          if (useQuarterlyData && hourData[`h${hour}_quarters`]) {
            // Count quarterly residuals
            const quarters = hourData[`h${hour}_quarters`];
            for (let q = 1; q <= 4; q++) {
              const quarterData = quarters[`q${q}`];
              if (quarterData && quarterData[`q${q}_residual`] > 0) {
                fixingIIResidualCount++;
              }
            }
          } else {
            // Count hourly residuals (legacy)
            const residualKey = `h${hour}_fixing_ii_residual`;
            if (hourData[residualKey] > 0) {
              fixingIIResidualCount++;
            }
          }
        }
      }
    }

    return (
      <div key={date} className={`border rounded-lg mb-4 overflow-hidden ${darkMode ? 'border-slate-700' : ''} ${isFiltered ? 'opacity-60' : ''}`}>
        <div
          className={`p-4 cursor-pointer transition-colors ${
            isFiltered ? (darkMode ? 'bg-slate-800/80 hover:bg-slate-700/80' : 'bg-slate-200 hover:bg-slate-300') :
            isIdle ? (darkMode ? 'bg-slate-900 hover:bg-slate-800' : 'bg-gray-50 hover:bg-gray-100') :
            isPnLPositive ? (darkMode ? 'bg-emerald-950/35 hover:bg-emerald-900/35' : 'bg-green-50 hover:bg-green-100') :
            isPnLNegative ? (darkMode ? 'bg-rose-950/40 hover:bg-rose-900/40' : 'bg-red-50 hover:bg-red-100') :
            (darkMode ? 'bg-slate-900 hover:bg-slate-800' : 'bg-gray-50 hover:bg-gray-100')
          }`}
          onClick={() => toggleRow(date)}
        >
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-center gap-3 md:gap-4">
              {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <span className={`font-semibold ${darkMode ? 'text-slate-100' : ''}`}>{date}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                data.action === 'buy' ? (darkMode ? 'bg-emerald-400/20 text-emerald-300 border border-emerald-400/30' : 'bg-green-200 text-green-800') :
                data.action === 'sell' ? (darkMode ? 'bg-rose-400/20 text-rose-200 border border-rose-400/30' : 'bg-red-200 text-red-800') :
                (darkMode ? 'bg-slate-700 text-slate-200 border border-slate-600' : 'bg-gray-200 text-gray-800')
              }`}>
                {data.action}
              </span>
              {fixingIIResidualCount > 0 && (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${darkMode ? 'bg-amber-400/15 text-amber-300 border border-amber-400/25' : 'bg-orange-100 text-orange-700'}`} title="Fixing II Residuals">
                  {fixingIIResidualCount} {new Date(date) >= new Date('2025-10-01') ? 'Q' : 'H'}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-5 gap-y-2 text-sm md:grid-cols-3 xl:grid-cols-5">
              <div className="min-w-[110px]">
                <span className={darkMode ? 'text-slate-400' : 'text-gray-500'}>Daily PnL:</span>
                <span className={`ml-2 font-semibold ${
                  data.daily_pnl > 0 ? 'text-green-600' : 
                  data.daily_pnl < 0 ? 'text-red-600' : 
                  darkMode ? 'text-slate-300' : 'text-gray-600'
                }`}>
                  {formatNumber(data.daily_pnl)}
                </span>
              </div>
              <div className="min-w-[92px]">
                <span className={darkMode ? 'text-slate-400' : 'text-gray-500'}>Base:</span>
                <span className={`ml-2 font-semibold ${darkMode ? 'text-slate-100' : ''}`}>{formatNumber(data.base_price)}</span>
              </div>
              <div className="min-w-[92px]">
                <span className={darkMode ? 'text-slate-400' : 'text-gray-500'}>Pred:</span>
                <span className={`ml-2 font-semibold ${darkMode ? 'text-slate-100' : ''}`}>{formatNumber(data.pred_avg)}</span>
              </div>
              <div className="min-w-[92px]">
                <span className={darkMode ? 'text-slate-400' : 'text-gray-500'}>Fix I:</span>
                <span className={`ml-2 font-semibold ${darkMode ? 'text-slate-100' : ''}`}>{formatNumber(data.fixing_i_price_avg)}</span>
              </div>
              <div className="min-w-[92px]">
                <span className={darkMode ? 'text-slate-400' : 'text-gray-500'}>Fix II:</span>
                <span className={`ml-2 font-semibold ${darkMode ? 'text-slate-100' : ''}`}>{formatNumber(data.fixing_ii_price_avg)}</span>
              </div>
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className={`p-4 border-t ${darkMode ? 'bg-slate-950/40 border-slate-700' : 'bg-white'}`}>
            {/* Top level data */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              {topLevelKeys.map(key => (
                <div key={key} className="text-sm">
                  <span className="text-gray-500">{key.replace(/_/g, ' ')}:</span>
                  <span className="ml-2 font-medium">{formatNumber(data[key])}</span>
                </div>
              ))}
            </div>
            
            {/* Detailed results sections */}
            {data.fixing_i_results && (
              <div className="mt-4">
                <h4 className="font-semibold text-gray-700 mb-3">Fixing I Results</h4>
                {renderFixingTable(data.fixing_i_results, 'I', data.action, formData.volume, date, data.base_price)}
              </div>
            )}

            {data.fixing_ii_results && (
              <div className="mt-4">
                <h4 className="font-semibold text-gray-700 mb-3">Fixing II Results {new Date(date) >= new Date('2025-10-01') && '(15-minute intervals)'}</h4>
                {renderFixingTable(data.fixing_ii_results, 'II', data.action, formData.volume, date, data.base_price)}
              </div>
            )}
            
            {data.balancing_market_results && (
              <div className="mt-4 p-3 bg-orange-50 rounded">
                <h4 className="font-semibold text-orange-800 mb-2">Balancing Market Results</h4>
                <div className="max-h-60 overflow-y-auto">
                  {renderDetailedResults(data.balancing_market_results)}
                </div>
              </div>
            )}
            
            {/* Forecast Data Section */}
            {data.forecasts && (
              <div className="mt-4 p-3 bg-teal-50 rounded">
                <h4 className="font-semibold text-teal-800 mb-2">Forecast Data</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="bg-white p-2 rounded">
                    <p className="text-xs text-gray-600">PSE PV Avg</p>
                    <p className="font-semibold">{formatNumber(data.forecasts.pse_pv_avg)}</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-xs text-gray-600">PSE Wind Avg</p>
                    <p className="font-semibold">{formatNumber(data.forecasts.pse_wind_avg)}</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-xs text-gray-600">ZRB Avg</p>
                    <p className="font-semibold">{formatNumber(data.forecasts.zrb_avg)}</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-xs text-gray-600">PV Δ 1d</p>
                    <p className={`font-semibold ${
                      data.forecasts.pse_pv_avg_delta_1d > 0 ? 'text-green-600' : 
                      data.forecasts.pse_pv_avg_delta_1d < 0 ? 'text-red-600' : 
                      'text-gray-600'
                    }`}>
                      {data.forecasts.pse_pv_avg_delta_1d > 0 ? '+' : ''}{formatNumber(data.forecasts.pse_pv_avg_delta_1d)}
                    </p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-xs text-gray-600">Wind Δ 1d</p>
                    <p className={`font-semibold ${
                      data.forecasts.pse_wind_avg_delta_1d > 0 ? 'text-green-600' : 
                      data.forecasts.pse_wind_avg_delta_1d < 0 ? 'text-red-600' : 
                      'text-gray-600'
                    }`}>
                      {data.forecasts.pse_wind_avg_delta_1d > 0 ? '+' : ''}{formatNumber(data.forecasts.pse_wind_avg_delta_1d)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Base Model Analysis</h1>
          <p className="text-gray-600">Run historical analysis with custom parameters</p>
        </div>

        {/* Input Form */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>Start Date</span>
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>End Date</span>
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Activity size={16} />
                <span>Spread</span>
              </label>
              <input
                type="number"
                value={formData.spread}
                onChange={(e) => handleInputChange('spread', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <BarChart3 size={16} />
                <span>Volume</span>
              </label>
              <input
                type="number"
                value={formData.volume}
                onChange={(e) => handleInputChange('volume', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Fixing I Margin Type</span>
              </label>
              <select
                value={formData.fixing_i_margin_type}
                onChange={(e) => handleInputChange('fixing_i_margin_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="fixed">Fixed Value</option>
                <option value="hourly_mae">Hourly MAE Based</option>
              </select>
            </div>

            {formData.fixing_i_margin_type === 'fixed' ? (
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <TrendingUp size={16} />
                  <span>Fixing I Margin</span>
                </label>
                <input
                  type="number"
                  value={formData.fixing_i_margin}
                  onChange={(e) => handleInputChange('fixing_i_margin', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            ) : (
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <TrendingUp size={16} />
                  <span>MAE Multiplier</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.fixing_i_mae_multiplier}
                  onChange={(e) => handleInputChange('fixing_i_mae_multiplier', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 0, 0.5, 1.0, 1.5, 2.0"
                />
              </div>
            )}

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Fixing II Margin</span>
              </label>
              <input
                type="number"
                value={formData.fixing_ii_margin}
                onChange={(e) => handleInputChange('fixing_ii_margin', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Model Version</span>
              </label>
              <select
                value={formData.model_version}
                onChange={(e) => handleInputChange('model_version', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {/* <option value="v0.2.0">v0.2.0</option>
                <option value="v0.2.1">v0.2.1</option>
                <option value="v0.2.2">v0.2.2</option>
                <option value="v0.2.3">v0.2.3</option>
                <option value="v0.2.3.1">v0.2.3.1</option>
                <option value="v0.2.3.2">v0.2.3.2</option>
                <option value="v0.2.4">v0.2.4</option>
                <option value="v0.2.5">v0.2.5</option>
                <option value="v0.2.5-stacked">v0.2.5-stacked</option>
                <option value="v0.2.6-stacked">v0.2.6-stacked</option>
                <option value="v0.2.5-stacked-weighted-l0-s1">v0.2.5-stacked-weighted-l0-s1</option>
                <option value="v0.2.5-stacked-weighted-l0.6-s0.4">v0.2.5-stacked-weighted-l0.6-s0.4</option>
                <option value="v0.2.6-stacked-warm-up">v0.2.6-stacked-warm-up</option>
                <option value="v0.2.6-stacked-warm-up-after-hp">v0.2.6-stacked-warm-up-after-hp</option>
                <option value="v0.2.6-stacked-warm-up-after-hp-ensemble10">v0.2.6-stacked-warm-up-after-hp-ensemble10</option>
                <option value="v0.2.4.1">v0.2.4.1</option>
                <option value="v0.2.7.1">v0.2.7.1</option>
                <option value="v0.2.7.2">v0.2.7.2</option>
                <option value="v0.3.0">v0.3.0</option>
                <option value="v0.3.1">v0.3.1</option>
                <option value="v0.4.0">v0.4.0</option>
                <option value="v0.4.1">v0.4.1</option> */}
                <option value="v1.0.0">v1.0.0</option>
                <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                <option value="v1.0.0-fix2-cp14">v1.0.0-fix2-cp14</option>
                <option value="v1.0.0-fix2-exaa">v1.0.0-fix2-exaa</option>
                <option value="v1.0.1">v1.0.1</option>
                <option value="v1.0.2">v1.0.2</option>
                <option value="v1.0.3">v1.0.3</option>
                <option value="v1.0.3.1">v1.0.3.1</option>
                <option value="v2.0.0">v2.0.0</option>
                <option value="v2.0.0-cp14">v2.0.0-cp14</option>
                <option value="v2.0.0-exaa">v2.0.0-exaa</option>
                <option value="v2.0.0-fix2">v2.0.0-fix2</option>
                <option value="v2.0.0-hourly">v2.0.0-hourly</option>
                <option value="v2.0.0-l1">v2.0.0-l1</option>
                <option value="v2.0.0-l1-fix2">v2.0.0-l1-fix2</option>
                <option value="v2.0.0-narx">v2.0.0-narx</option>
                <option value="v2.0.0-narx-single">v2.0.0-narx-single</option>
                <option value="v2.0.0-narx-fix2">v2.0.0-narx-fix2</option>
              </select>
            </div>
          </div>

          <div className="mt-6 flex gap-4">
            <button
              onClick={() => runBacktesting()}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 transform hover:scale-[1.02]'
              }`}
            >
              <Play size={20} />
              <span>{isLoading ? 'Running Analysis...' : 'Run Analysis'}</span>
            </button>
            
            <button
              onClick={() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                const tomorrowStr = formatDate(tomorrow);
                const updatedFormData = {
                  ...formData,
                  start_date: tomorrowStr,
                  end_date: tomorrowStr
                };
                setFormData(updatedFormData);
                // Run analysis with the updated data
                runBacktesting(updatedFormData);
              }}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 transform hover:scale-[1.02]'
              }`}
            >
              <Calendar size={20} />
              <span>{isLoading ? 'Running Analysis...' : 'Run Analysis For Tomorrow'}</span>
            </button>
          </div>

          {/* Hour display format toggle */}
          <div className="mt-4 flex items-center space-x-3">
            <span className="text-sm text-gray-700">Display hours as 1-24</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={displayHours1to24}
                onChange={(e) => setDisplayHours1to24(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Results Summary */}
        {results && results.summary && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Summary</h2>
            
            {/* First Row: Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Total PnL</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.total_pnl)}
                    </p>
                  </div>
                  <DollarSign className="text-blue-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Total Days</p>
                    <p className="text-2xl font-bold text-purple-800">
                      {results.summary.total_days}
                    </p>
                  </div>
                  <Calendar className="text-purple-500" size={32} />
                </div>
              </div>


              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Profitable Days</p>
                    <p className="text-2xl font-bold text-green-800">
                      {results.summary.profitable_action_days}
                    </p>
                    <p className="text-xs text-green-600">
                      {formatPercentage((results.summary.profitable_action_days / results.summary.action_days_count) * 100)} of action days
                    </p>
                  </div>
                  <TrendingUp className="text-green-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600 font-medium">Loss Days</p>
                    <p className="text-2xl font-bold text-red-800">
                      {results.summary.loss_action_days}
                    </p>
                    <p className="text-xs text-red-600">
                      {formatPercentage((results.summary.loss_action_days / results.summary.action_days_count) * 100)} of action days
                    </p>
                  </div>
                  <TrendingDown className="text-red-500" size={32} />
                </div>
              </div>
            </div>

            {/* Second Row: Additional Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-yellow-700 font-medium">Avg Profit/Action Day</p>
                    <p className={`text-xl font-bold ${
                      results.summary.avg_profit_per_action_day >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.avg_profit_per_action_day)}
                    </p>
                    <p className="text-xs text-gray-600">
                      Calculated for {results.summary.action_days_count} action days
                    </p>
                  </div>
                  <Target className="text-yellow-600" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600 font-medium">Buy Actions</p>
                    <p className="text-2xl font-bold text-emerald-800">
                      {results.summary.action_breakdown?.buy?.count || 0}
                    </p>
                    <p className="text-xs text-emerald-600">
                      {formatPercentage(results.summary.action_breakdown?.buy?.percentage || 0)}
                    </p>
                  </div>
                  <TrendingUp className="text-emerald-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-rose-600 font-medium">Sell Actions</p>
                    <p className="text-2xl font-bold text-rose-800">
                      {results.summary.action_breakdown?.sell?.count || 0}
                    </p>
                    <p className="text-xs text-rose-600">
                      {formatPercentage(results.summary.action_breakdown?.sell?.percentage || 0)}
                    </p>
                  </div>
                  <TrendingDown className="text-rose-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Idle Days</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {results.summary.action_breakdown?.idle?.count || 0}
                    </p>
                    <p className="text-xs text-gray-600">
                      {formatPercentage(results.summary.action_breakdown?.idle?.percentage || 0)}
                    </p>
                  </div>
                  <Activity className="text-gray-500" size={32} />
                </div>
              </div>
            </div>

            {/* Prediction Accuracy Section */}
            {results.summary.prediction_accuracy && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                  <AlertCircle className="mr-2" size={20} />
                  Prediction Accuracy Analysis
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Left Column - Prediction Bias */}
                  <div>
                    <div className="bg-white rounded p-3 mb-4">
                      <p className="text-sm text-gray-600 mb-2">Prediction Bias</p>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Underpredictions:</span>
                          <span className="font-medium">{results.summary.prediction_accuracy.underprediction_count} ({formatPercentage(results.summary.prediction_accuracy.underprediction_percentage)})</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Overpredictions:</span>
                          <span className="font-medium">{results.summary.prediction_accuracy.overprediction_count} ({formatPercentage(results.summary.prediction_accuracy.overprediction_percentage)})</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Column - Error Distribution */}
                  {results.summary.prediction_accuracy.prediction_error_distribution && (
                    <div className="bg-white rounded p-3">
                      <p className="text-sm text-gray-600 mb-2">Error Distribution</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Mean Error:</span>
                          <span className="font-medium">{formatNumber(results.summary.prediction_accuracy.prediction_error_distribution.mean)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Std Dev:</span>
                          <span className="font-medium">{formatNumber(results.summary.prediction_accuracy.prediction_error_distribution.std)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Min Error:</span>
                          <span className="font-medium">{formatNumber(results.summary.prediction_accuracy.prediction_error_distribution.min)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Max Error:</span>
                          <span className="font-medium">{formatNumber(results.summary.prediction_accuracy.prediction_error_distribution.max)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Median (P50):</span>
                          <span className="font-medium">{formatNumber(results.summary.prediction_accuracy.prediction_error_distribution.percentile_50)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>P25 - P75:</span>
                          <span className="font-medium">
                            {formatNumber(results.summary.prediction_accuracy.prediction_error_distribution.percentile_25)} - 
                            {formatNumber(results.summary.prediction_accuracy.prediction_error_distribution.percentile_75)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Prediction vs Fixing Statistics */}
            {results.summary.prediction_vs_fixing_stats && (
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                  <BarChart3 className="mr-2" size={20} />
                  Prediction vs Fixing Price Statistics
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Fix I Statistics */}
                  <div className="bg-white rounded p-3">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Model vs Fix I</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Mean Error (Bias):</span>
                        <span className={`font-medium ${
                          results.summary.prediction_vs_fixing_stats.fix_i.mean_error > 0 ? 'text-red-600' : 
                          results.summary.prediction_vs_fixing_stats.fix_i.mean_error < 0 ? 'text-blue-600' : 
                          'text-gray-600'
                        }`}>
                          {formatNumber(results.summary.prediction_vs_fixing_stats.fix_i.mean_error)}
                          {results.summary.prediction_vs_fixing_stats.fix_i.mean_error > 0 && ' (overpredicting)'}
                          {results.summary.prediction_vs_fixing_stats.fix_i.mean_error < 0 && ' (underpredicting)'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">RMSE:</span>
                        <span className="font-medium">{formatNumber(results.summary.prediction_vs_fixing_stats.fix_i.rmse)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Std Dev (unbiased):</span>
                        <span className="font-medium">{formatNumber(results.summary.prediction_vs_fixing_stats.fix_i.std_unbiased)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Std Dev (population):</span>
                        <span className="font-medium">{formatNumber(results.summary.prediction_vs_fixing_stats.fix_i.std_population)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Fix II Statistics */}
                  <div className="bg-white rounded p-3">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Model vs Fix II</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Mean Error (Bias):</span>
                        <span className={`font-medium ${
                          results.summary.prediction_vs_fixing_stats.fix_ii.mean_error > 0 ? 'text-red-600' : 
                          results.summary.prediction_vs_fixing_stats.fix_ii.mean_error < 0 ? 'text-blue-600' : 
                          'text-gray-600'
                        }`}>
                          {formatNumber(results.summary.prediction_vs_fixing_stats.fix_ii.mean_error)}
                          {results.summary.prediction_vs_fixing_stats.fix_ii.mean_error > 0 && ' (overpredicting)'}
                          {results.summary.prediction_vs_fixing_stats.fix_ii.mean_error < 0 && ' (underpredicting)'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">RMSE:</span>
                        <span className="font-medium">{formatNumber(results.summary.prediction_vs_fixing_stats.fix_ii.rmse)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Std Dev (unbiased):</span>
                        <span className="font-medium">{formatNumber(results.summary.prediction_vs_fixing_stats.fix_ii.std_unbiased)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Std Dev (population):</span>
                        <span className="font-medium">{formatNumber(results.summary.prediction_vs_fixing_stats.fix_ii.std_population)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Hourly MAE Statistics */}
            {results.summary.hourly_mae_stats && (
              <div className="bg-indigo-50 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                  <Activity className="mr-2" size={20} />
                  Hourly Mean Absolute Error (MAE) Analysis
                </h3>
                
                {/* Overall MAE */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white rounded p-3">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Overall Hourly MAE vs Fix I</p>
                    <p className="text-2xl font-bold text-indigo-600">
                      {formatNumber(results.summary.hourly_mae_stats.fix_i.overall_mae)}
                    </p>
                    <p className="text-xs text-gray-500">Across all hours</p>
                  </div>
                  <div className="bg-white rounded p-3">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Overall Hourly MAE vs Fix II</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatNumber(results.summary.hourly_mae_stats.fix_ii.overall_mae)}
                    </p>
                    <p className="text-xs text-gray-500">Across all hours</p>
                  </div>
                </div>
                
                {/* Hourly Breakdown */}
                <div className="bg-white rounded p-3">
                  <p className="text-sm font-semibold text-gray-700 mb-3">MAE by Hour</p>
                  <div className="space-y-4">
                    {/* Fix I Hourly MAE */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-2">Fix I MAE by Hour</p>
                      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                        {(() => {
                          // Collect all Fix I MAE values for color scaling
                          const allFixIMAEValues = Array.from({ length: 24 }, (_, h) => 
                            results.summary.hourly_mae_stats.fix_i.hourly_mae[`h${h}`]
                          );
                          
                          return Array.from({ length: 24 }, (_, hour) => {
                            const maeValue = results.summary.hourly_mae_stats.fix_i.hourly_mae[`h${hour}`];
                            const colorClass = getMAEColorClass(maeValue, allFixIMAEValues);
                            
                            return (
                              <div key={`fix-i-${hour}`} className={`${colorClass} rounded p-2 text-center transition-colors`}>
                                <p className="text-xs font-medium text-gray-700">H{displayHours1to24 ? hour + 1 : hour}</p>
                                <p className="text-xs font-semibold text-gray-800">
                                  {formatNumber(maeValue) || 'N/A'}
                                </p>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                    
                    {/* Fix II Hourly MAE */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-2">Fix II MAE by Hour</p>
                      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                        {(() => {
                          // Collect all Fix II MAE values for color scaling
                          const allFixIIMAEValues = Array.from({ length: 24 }, (_, h) => 
                            results.summary.hourly_mae_stats.fix_ii.hourly_mae[`h${h}`]
                          );
                          
                          return Array.from({ length: 24 }, (_, hour) => {
                            const maeValue = results.summary.hourly_mae_stats.fix_ii.hourly_mae[`h${hour}`];
                            const colorClass = getMAEColorClass(maeValue, allFixIIMAEValues);
                            
                            return (
                              <div key={`fix-ii-${hour}`} className={`${colorClass} rounded p-2 text-center transition-colors`}>
                                <p className="text-xs font-medium text-gray-700">H{displayHours1to24 ? hour + 1 : hour}</p>
                                <p className="text-xs font-semibold text-gray-800">
                                  {formatNumber(maeValue) || 'N/A'}
                                </p>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                  
                  {/* Color Legend */}
                  <div className="mt-4 pt-3 border-t">
                    <p className="text-xs text-gray-600 mb-2">Color Scale:</p>
                    <div className="flex items-center space-x-2 text-xs">
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-green-100 rounded mr-1"></div>
                        <span>Low MAE</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-yellow-50 rounded mr-1"></div>
                        <span>Medium</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-red-100 rounded mr-1"></div>
                        <span>High MAE</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Expandable Residuals Section */}
            <div className={`border rounded-lg overflow-hidden ${darkMode ? 'border-slate-700' : ''}`}>
              <div
                className={`p-4 cursor-pointer transition-colors ${darkMode ? 'bg-slate-900 hover:bg-slate-800' : 'bg-gray-50 hover:bg-gray-100'}`}
                onClick={() => setExpandedResiduals(!expandedResiduals)}
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center space-x-3">
                    {expandedResiduals ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-slate-100' : 'text-gray-700'}`}>Residuals Summary</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 sm:gap-6">
                    <div className="text-left sm:text-right">
                      <p className="text-sm text-orange-600 font-medium">Fix I Total</p>
                      <p className="text-lg font-bold text-orange-800">
                        {formatNumber(results.summary.total_fixing_i_residual)}
                      </p>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="text-sm text-pink-600 font-medium">Fix II Total</p>
                      <p className="text-lg font-bold text-pink-800">
                        {formatNumber(results.summary.total_fixing_ii_residual)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {expandedResiduals && (
                <div className={`p-4 border-t ${darkMode ? 'bg-slate-950/40 border-slate-700' : 'bg-white'}`}>
                  {(() => {
                    const residualDates = getResidualDates();
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-orange-700 mb-2">Fixing I Residual Dates</h4>
                          {residualDates.fixI.length > 0 ? (
                            <div className="bg-orange-50 rounded p-3">
                              <p className="text-sm text-gray-600 mb-2">
                                {residualDates.fixI.length} days with residuals:
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {residualDates.fixI.map(date => (
                                  <span key={date} className="px-2 py-1 bg-orange-200 text-orange-800 rounded text-xs">
                                    {date}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No residuals found</p>
                          )}
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-pink-700 mb-2">Fixing II Residual Dates</h4>
                          {residualDates.fixII.length > 0 ? (
                            <div className="bg-pink-50 rounded p-3">
                              <p className="text-sm text-gray-600 mb-2">
                                {residualDates.fixII.length} days with residuals:
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {residualDates.fixII.map(date => (
                                  <span key={date} className="px-2 py-1 bg-pink-200 text-pink-800 rounded text-xs">
                                    {date}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No residuals found</p>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Filters Section */}
            <div className={`border rounded-lg overflow-hidden mt-6 ${darkMode ? 'border-slate-700' : ''}`}>
              <div
                className={`p-4 cursor-pointer transition-colors ${darkMode ? 'bg-slate-900 hover:bg-slate-800' : 'bg-gray-50 hover:bg-gray-100'}`}
                onClick={() => setExpandedFilters(!expandedFilters)}
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center space-x-3">
                    {expandedFilters ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-slate-100' : 'text-gray-700'}`}>Filters</h3>
                    <Filter size={20} className={darkMode ? 'text-slate-400' : 'text-gray-600'} />
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    Filter daily results based on forecast values
                  </p>
                </div>
              </div>
              
              {expandedFilters && (
                <div className={`p-4 border-t ${darkMode ? 'bg-slate-950/40 border-slate-700' : 'bg-white'}`}>
                  <div className="space-y-4">
                    {/* PSE PV Average Filter */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        PSE PV Average: {formatNumber(filters.pse_pv_avg.min)} - {formatNumber(filters.pse_pv_avg.max)}
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="range"
                          min={filterBounds.pse_pv_avg.min}
                          max={filterBounds.pse_pv_avg.max}
                          value={filters.pse_pv_avg.min}
                          onChange={(e) => handleFilterChange('pse_pv_avg', 'min', e.target.value)}
                          className="flex-1"
                        />
                        <input
                          type="range"
                          min={filterBounds.pse_pv_avg.min}
                          max={filterBounds.pse_pv_avg.max}
                          value={filters.pse_pv_avg.max}
                          onChange={(e) => handleFilterChange('pse_pv_avg', 'max', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>

                    {/* PSE Wind Average Filter */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        PSE Wind Average: {formatNumber(filters.pse_wind_avg.min)} - {formatNumber(filters.pse_wind_avg.max)}
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="range"
                          min={filterBounds.pse_wind_avg.min}
                          max={filterBounds.pse_wind_avg.max}
                          value={filters.pse_wind_avg.min}
                          onChange={(e) => handleFilterChange('pse_wind_avg', 'min', e.target.value)}
                          className="flex-1"
                        />
                        <input
                          type="range"
                          min={filterBounds.pse_wind_avg.min}
                          max={filterBounds.pse_wind_avg.max}
                          value={filters.pse_wind_avg.max}
                          onChange={(e) => handleFilterChange('pse_wind_avg', 'max', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>

                    {/* ZRB Average Filter */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        ZRB Average: {formatNumber(filters.zrb_avg.min)} - {formatNumber(filters.zrb_avg.max)}
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="range"
                          min={filterBounds.zrb_avg.min}
                          max={filterBounds.zrb_avg.max}
                          value={filters.zrb_avg.min}
                          onChange={(e) => handleFilterChange('zrb_avg', 'min', e.target.value)}
                          className="flex-1"
                        />
                        <input
                          type="range"
                          min={filterBounds.zrb_avg.min}
                          max={filterBounds.zrb_avg.max}
                          value={filters.zrb_avg.max}
                          onChange={(e) => handleFilterChange('zrb_avg', 'max', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>

                    {/* PSE PV Delta 1d Filter */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        PSE PV Δ 1d: {formatNumber(filters.pse_pv_avg_delta_1d.min)} - {formatNumber(filters.pse_pv_avg_delta_1d.max)}
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="range"
                          min={filterBounds.pse_pv_avg_delta_1d.min}
                          max={filterBounds.pse_pv_avg_delta_1d.max}
                          value={filters.pse_pv_avg_delta_1d.min}
                          onChange={(e) => handleFilterChange('pse_pv_avg_delta_1d', 'min', e.target.value)}
                          className="flex-1"
                        />
                        <input
                          type="range"
                          min={filterBounds.pse_pv_avg_delta_1d.min}
                          max={filterBounds.pse_pv_avg_delta_1d.max}
                          value={filters.pse_pv_avg_delta_1d.max}
                          onChange={(e) => handleFilterChange('pse_pv_avg_delta_1d', 'max', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>

                    {/* PSE Wind Delta 1d Filter */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        PSE Wind Δ 1d: {formatNumber(filters.pse_wind_avg_delta_1d.min)} - {formatNumber(filters.pse_wind_avg_delta_1d.max)}
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="range"
                          min={filterBounds.pse_wind_avg_delta_1d.min}
                          max={filterBounds.pse_wind_avg_delta_1d.max}
                          value={filters.pse_wind_avg_delta_1d.min}
                          onChange={(e) => handleFilterChange('pse_wind_avg_delta_1d', 'min', e.target.value)}
                          className="flex-1"
                        />
                        <input
                          type="range"
                          min={filterBounds.pse_wind_avg_delta_1d.min}
                          max={filterBounds.pse_wind_avg_delta_1d.max}
                          value={filters.pse_wind_avg_delta_1d.max}
                          onChange={(e) => handleFilterChange('pse_wind_avg_delta_1d', 'max', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    
                    {/* Reset Filters Button */}
                    <div className="pt-2">
                      <button
                        onClick={() => {
                          const resetFilters = {};
                          Object.keys(filterBounds).forEach(key => {
                            resetFilters[key] = { min: filterBounds[key].min, max: filterBounds[key].max };
                          });
                          setFilters(resetFilters);
                        }}
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                      >
                        Reset All Filters
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Daily Results */}
        {results && results.results && (
          <div className={`${darkMode ? 'bg-slate-900 border border-slate-800 shadow-xl shadow-slate-950/20' : 'bg-white shadow-sm'} rounded-xl p-6`}>
            <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>Daily Results</h2>
            <div className="space-y-2">
              {Object.entries(results.results)
                .filter(([key]) => key !== 'summary')
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([date, data]) => renderDateRow(date, data))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Backtesting;
